import { useCallback, useMemo } from "react";
import isFunction from "../utils/isFunction";
import useCache, { CACHE } from "./useCache";
import useViewTrack from "./useViewTrack";

// Singular, not plural
export const TRACKED_TARGET = {
	WIDGET: "WIDGET",

	PRODUCT: "PRODUCT",
	BRAND: "BRAND",
	BRAND_ROW: "BRAND_ROW",
	PRODUCT_CARD: "PRODUCT_CARD",
	BRAND_ROW_OFFER: "BRAND_ROW_OFFER",
	BRAND_SAVER: "BRAND_SAVER",
	BRAND_LEAD_TILE: "BRAND_LEAD_TILE",

	VIEWABLE_THRESHOLD_WIDGET: "VIEWABLE_THRESHOLD_WIDGET",
	VIEWABLE_THRESHOLD_BRAND: "VIEWABLE_THRESHOLD_BRAND",
	VIEWABLE_THRESHOLD_PRODUCT: "VIEWABLE_THRESHOLD_PRODUCT",

	BRAND_ROW_SCROLL: "BRAND_ROW_SCROLL",
};

// Note: The hook is called useCache"View"Track
// but may also be used for cache based click / engagement event tracking in general (See usage for BRAND_ROW_SCROLL)
const useCachedViewTrack = ({ id, key, onTrack, canTrack, ...rest } = {}) => {
	const { data: trackedTargets, setData: setTrackedTargets } = useCache({
		key: CACHE.TRACKED_TARGETS,
	});

	const doesEntityExist = useMemo(
		() => key in trackedTargets,
		[key, trackedTargets]
	);
	const hasBeenTracked = useMemo(
		() => (!doesEntityExist ? null : id in (trackedTargets?.[key] || {})),
		[trackedTargets, id, key, doesEntityExist]
	);

	const cacheTrackedValue = useCallback(
		() =>
			!doesEntityExist
				? null
				: setTrackedTargets((trackedTargets) => ({
						...trackedTargets,
						[key]: { ...trackedTargets[key], [id]: true },
				  })),
		[setTrackedTargets, key, id, doesEntityExist]
	);

	const handleTrack = useCallback(() => {
		if (isFunction(onTrack)) onTrack();
		cacheTrackedValue();
	}, [onTrack, cacheTrackedValue]);

	const canTrackEvent = useMemo(() => {
		if (hasBeenTracked) return false;
		if (typeof canTrack !== "undefined") return canTrack;
		return true;
	}, [hasBeenTracked, canTrack]);

	useViewTrack({
		...rest,
		canTrack: canTrackEvent,
		onTrack: handleTrack,
	});

	return {
		hasBeenTracked,
		handleTrack: cacheTrackedValue,
		doesEntityExist,
		data: doesEntityExist ? trackedTargets[key] : null,
	};
};

export default useCachedViewTrack;
